import { useState } from 'react';
import { Banner, Button, DataTable, Field, Input, Select, Td, Textarea } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { DELIVERED_TO_LABELS, fmtDate, todayISO } from '../../lib/format';
import type { DeliveredTo, JobDetail } from '../../shared/api';
import type { TabProps } from './types';

const KINDS = {
  labor: 'First / ongoing labor',
  delivery: 'Materials delivery',
  measure: 'Measure / template visit',
  fabrication: 'Fabrication started',
  punchlist: 'Punchlist',
  warranty: 'Warranty work',
  final_complete: 'Scope complete (locks last work date)',
} as const;
type Kind = keyof typeof KINDS;

/** Only the events that make sense for what this job furnishes. */
export function kindsFor(job: Pick<JobDetail, 'workType' | 'speciallyFabricated'>): Kind[] {
  return (Object.keys(KINDS) as Kind[]).filter((k) => {
    if (k === 'labor') return job.workType !== 'materials_only';
    if (k === 'delivery') return job.workType !== 'labor_only';
    if (k === 'fabrication') return job.workType !== 'labor_only' && !!job.speciallyFabricated;
    return true;
  });
}

/**
 * The furnishing log the deadline engine reads. Punchlist and warranty entries
 * deliberately do not extend the lien clock — Florida counts final furnishing
 * of the original scope.
 */
export default function WorkLogTab({ job, reload }: TabProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const kinds = kindsFor(job);
  const blank = () => ({ kind: kinds[0], date: todayISO(), note: '', deliveredTo: 'job_site' as DeliveredTo });
  const [draft, setDraft] = useState(blank);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/jobs/${job.id}/work-events`, {
        kind: draft.kind,
        date: draft.date,
        note: draft.note || undefined,
        deliveredTo: draft.kind === 'delivery' ? draft.deliveredTo : undefined,
      });
      setDraft(blank());
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the work event');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_20rem]">
      <div>
        <DataTable head={['Date', 'Kind', 'Note']} empty="No work events logged yet.">
          {[...job.workEvents]
            .sort((a, b) => b.eventDate.localeCompare(a.eventDate))
            .map((w) => (
              <tr key={w.id} className="border-b border-border/60">
                <Td className="whitespace-nowrap font-medium">{fmtDate(w.eventDate)}</Td>
                <Td className="text-muted">
                  {KINDS[w.kind as Kind] ?? w.kind}
                  {w.kind === 'delivery' && w.deliveredTo ? ` — ${DELIVERED_TO_LABELS[w.deliveredTo]}` : ''}
                </Td>
                <Td className="text-muted">{w.note ?? '—'}</Td>
              </tr>
            ))}
        </DataTable>
      </div>

      <form onSubmit={submit} className="space-y-4 rounded-lg border border-border bg-surface-alt/40 p-4">
        <h3 className="text-sm font-semibold text-ink">Add work event</h3>
        {error && <Banner tone="urgent">{error}</Banner>}
        <Field label="Kind">
          <Select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as Kind })}>
            {kinds.map((k) => (
              <option key={k} value={k}>
                {KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        {draft.kind === 'delivery' && (
          <Field label="Delivered to">
            <Select value={draft.deliveredTo} onChange={(e) => setDraft({ ...draft, deliveredTo: e.target.value as DeliveredTo })}>
              {(Object.keys(DELIVERED_TO_LABELS) as DeliveredTo[]).map((d) => (
                <option key={d} value={d}>
                  {DELIVERED_TO_LABELS[d]}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Date">
          <Input required type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
        </Field>
        <Field label="Note (optional)">
          <Textarea value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
        </Field>
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Saving…' : 'Add event'}
        </Button>
      </form>
    </div>
  );
}
